
import React from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

interface PaginationProps {
    page: number
    setPage: (page: number | ((prev: number) => number)) => void
    pageSize: number
    setPageSize: (pageSize: number) => void
    totalItems: number
    className?: string
}

export const Pagination: React.FC<PaginationProps> = ({
    page,
    setPage,
    pageSize,
    setPageSize,
    totalItems,
    className = ''
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
    const startIdx = (page - 1) * pageSize
    const endIdx = Math.min(startIdx + pageSize, totalItems)

    // Helper for page change to ensure within bounds
    const handlePageChange = (newPage: number) => {
        setPage(Math.max(1, Math.min(newPage, totalPages)))
    }

    return (
        <div className={`px-5 py-4 border-t border-gray-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50 dark:bg-slate-900/50 ${className}`}>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>Mostrar:</span>
                <select
                    className="border rounded-md px-2 py-1 bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
                <span>por página</span>
            </div>

            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2 hidden sm:inline">
                    Página {page} de {totalPages}
                </span>

                <div className="flex items-center gap-1">
                    <button
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-slate-800 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700"
                        onClick={() => handlePageChange(1)}
                        disabled={page <= 1}
                        title="Primera página"
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-slate-800 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page <= 1}
                        title="Página anterior"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    {/* Page Numbers */}
                    <div className="hidden sm:flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (page <= 3) {
                                pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = page - 2 + i;
                            }

                            return (
                                <button
                                    key={pageNum}
                                    className={`inline-flex items-center justify-center h-8 w-8 rounded-md text-sm font-medium transition-colors
                            ${page === pageNum
                                            ? 'bg-blue-600 text-white border border-blue-600 dark:bg-blue-600 dark:border-blue-600'
                                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700'
                                        }`}
                                    onClick={() => handlePageChange(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-slate-800 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages}
                        title="Página siguiente"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-slate-800 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page >= totalPages}
                        title="Última página"
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    )
}
